import React from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { get, isEmpty } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';
import TextArea from '../../components/FormFields/TextArea';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as ServiceManagementActions from '../../actions/serviceManagementActions';
import * as UiActions from '../../actions/uiActions';

import classes from './EditServiceCallModal.module.css';

const ValidationSchema = Yup.object().shape({
    summary: Yup.string()
        .required(),
    description: Yup.string()
});

const EditServiceCallModal = ({
    activeItem,
    close,
    clearErrors,
    open,
    title,
    isLoading,
    toggleLoading,
    errors,
    ...props
}) => {
    const formik = useFormik({
        initialValues: {
            summary:     get(activeItem, 'summary', ''),
            description: get(activeItem, 'description', '')
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                updateActiveCall
            } = props;

            let isUpdated;

            const body = {
                summary:     values.summary,
                description: values.description
            };

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isUpdated = updateActiveCall(body);
                }

                if (isUpdated) {
                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    if (isEmpty(activeItem)) return null;

    return (
        <EsmsDialog
            onClose={close}
            disabled={formik.isSubmitting}
            onConfirm={formik.handleSubmit}
            confirmText="Update"
            open={open}
            isLoading={isLoading}
            title={title}
            cancelable={true}
            errors={errors}
            onRendered={formik.handleReset}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/SettingsFlat.svg" alt="Service Call Icon" />
                        <span>Edit Service Request</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Provide a service request summary.
                        </p>
                        <p>
                            Description is optional.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="summary"
                                name="summary"
                                type="text"
                                value={formik.values.summary}
                                label="Summary"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error: classes.cssErrorUnderline,
                                        root:  classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.summary) && Boolean(formik.touched.summary)}
                                helperText={Boolean(formik.touched.summary) && formik.errors.summary}
                            />
                        </FormControl>
                        <FormControl className={classes.fullWidth}>
                            <TextArea
                                name="description"
                                type="text"
                                multiline={true}
                                value={formik.values.description}
                                label="Description"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error: classes.cssErrorUnderline,
                                        root:  classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.description) && Boolean(formik.touched.description)}
                                helperText={Boolean(formik.touched.description) && formik.errors.description}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

EditServiceCallModal.defaultProps = {
    activeItem:       {},
    clearErrors:      () => {},
    close:            () => {},
    errors:           [],
    isLoading:        false,
    open:             false,
    title:            '',
    updateActiveCall: () => {}
};

EditServiceCallModal.propTypes = {
    activeItem:       object,
    clearErrors:      func,
    close:            func,
    errors:           array,
    isLoading:        bool,
    open:             bool,
    title:            string,
    updateActiveCall: func
};

const mapStateToProps = state => {
    return {
        activeItem: state.svcManagement.activeItem,
        errors:     state.messages.errors,
        isLoading:  state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...ServiceManagementActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditServiceCallModal);
